/**
 * EstateFaqSection — the estate's FAQ, in the document (SEO F3 / B.3b).
 *
 * The backend has minted `FAQPage` JSON-LD for estates since SEO F3: six
 * possible questions (seven until #8803 step 3 retired the village-house one,
 * D-388), each answered ONLY from a stored fact, each answer a
 * whole sentence — "{station} station is about {distance} m from {estate} in
 * a straight line". The page rendered a facts grid (phase / blocks / lift /
 * village) and no Q&A at all, and the nearest-MTR and nearest-school sentences
 * two of those questions answer appeared in no served byte of the page.
 *
 * That is the same structured-data policy violation as the accordion class
 * (#4749), reached from the other end: there the renderer dropped the answers,
 * here there was no renderer. Google requires `FAQPage` content to be visible
 * on the page, and estate pages are the programmatic scale-out corpus — the
 * largest indexable surface on the site.
 *
 * The `faqs` are a {@link PageFaqs}: the items AND the JSON-LD describing
 * exactly them, minted together server-side and carried across the wire as one
 * `seo.v1.SeoFaq` message. The page hands the same value to `SeoHead` and
 * to this section, so the described answers and the rendered answers are one
 * array — and `FaqDisclosureList` serves them from a native `<details>`, which
 * has no closed-state child dropping (`shared/components/FaqDisclosureList`).
 */
import { FaqDisclosureList } from '@/shared/components/FaqDisclosureList';
import { useTranslations } from '@/shared/i18n';
import type { PageFaqs } from '@/shared/lib/seo/faq-jsonld';

export function EstateFaqSection({ faqs }: { faqs: PageFaqs }) {
  const t = useTranslations('estates.detail');

  return (
    <section
      aria-labelledby="estate-faqs-heading"
      className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
    >
      <h2 id="estate-faqs-heading" className="mb-6 text-2xl font-bold">
        {t('faqs.title')}
      </h2>
      <div className="max-w-3xl">
        <FaqDisclosureList faqs={faqs} />
      </div>
    </section>
  );
}
